import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetDeviceEventDetailsQuery,
  useGetDeviceEventsListQuery,
  useGetDeviceListingQuery,
  useRequestVideoMutation,
} from "../../../api/network/camerasAdminVMApiService";
import {
  downloadEventVideo,
  useEventGetVideoDetailsMutation,
  useGetCameraConfigQuery,
  useGetCameraDetailsQuery,
  useGetEventCommentsQuery,
  useGetEventDetailsQuery,
  useGetEventVideoDownloadUrlMutation,
  useGetTaskStatusByIdQuery,
} from "../../../api/network/vmApiService";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import {
  CameraEventGetVideoDetailResponse,
  CameraEventGetVideoDetailResponseVideos,
  DeviceEvent,
  DeviceListingResponse,
} from "../../../api/types/Camera";
import {
  DeviceSnapshot,
  EventComment,
} from "../../../api/types/CamerasAdminVM";
import { ListingResponse } from "../../../api/types/Listing";
import svgIcons from "../../../assets/svg/svgIcons";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import AppAvatar from "../../../components/avatar";
import { APP_CONFIG } from "../../../constants/constants";
import { useAccountTimezone } from "../../../hooks/timezone";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  getStartEndOfDayInUTC,
  humanReadableTimeDuration,
  jsTimeToDBTime,
} from "../../../utils/dateTime";
import { convertKmphTo } from "../../../utils/distance";
import { useLoggedInUserData } from "../../../utils/user";
import {
  SPEED_UNIT_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
} from "../../admin-details/account/constants";
import ModalCameraVideo from "../../map-asset/modalCameraVideo";
import { TRecordingDetails } from "../../map-asset/type";
import AppVideoElement from "../components/videoElement";
import {
  CAMERA_CAPTURE_MODES,
  IGNORED_EVENTS,
  RECORDING_TYPE,
  RENAMED_EVENTS,
} from "../constants";
import ParkingPhotos from "../management/modalParkingPhotos";
import ModalDownloadConfirmation from "./modalDownloadConfirmation";
import ModalEventComments from "./modalEventComments";
import CameraVideoHistoryEventsMap from "./video-history-events-map";

const EventSpecItem = ({ title, value }: { title: string; value: string }) => {
  return (
    <div className="col-span-4 flex flex-col gap-2">
      <span className="text-heading-gray666 font-medium">{title}</span>
      <span>{value}</span>
    </div>
  );
};

enum VIDEO_PLAYBACK_STATE {
  playing = "playing",
  paused = "paused",
  not_started = "not_started",
}

const ScreenCameraVideoHistoryEventDetails: FC = () => {
  const { deviceEventId } = useParams<{ deviceEventId: string }>();
  const [cameraId, eventRecordId] = (deviceEventId || "")?.split("__");

  const location = useLocation();
  const { cameraListItem, ...deviceEventFromState } =
    location?.state as DeviceEvent & {
      cameraListItem: DeviceListingResponse | undefined;
    };

  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });
  const dispatch = useDispatch();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playbackState, setPlaybackState] = useState<VIDEO_PLAYBACK_STATE>(
    VIDEO_PLAYBACK_STATE.not_started
  );
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );

  // retrieve account timezone
  const { convertToTZForDateFns, tzForDateFns, tzForDisplay } =
    useAccountTimezone();

  // speed unit
  const orgDistanceUnit = useLoggedInUserData(
    "speedUnits"
  ) as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgDistanceUnit)
      ?.unit || SPEED_UNITS_OPTIONS[0].unit;

  const {
    data: dataDeviceEventDetails,
    isFetching: isFetchingDeviceEventDetails,
  } = useGetDeviceEventDetailsQuery(
    deviceEventFromState?.recordid?.toString() || "",
    {
      // skip: !deviceEventFromState?.recordid,
    }
  );

  // on page load, scroll to the top
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const { data: dataEventDetails, isFetching: isFetchingEventDetails } =
    useGetEventDetailsQuery(deviceEventFromState?.ticket || "", {
      skip: !deviceEventFromState?.ticket,
    });
  const [commentsForTicket, setCommentsForTicket] = useState<string | null>(
    null
  );

  const { data: dataEventComments, isFetching: isFetchingEventComments } =
    useGetEventCommentsQuery(deviceEventFromState?.ticket || "", {
      skip: !deviceEventFromState?.ticket,
    });

  const [eventVideoDetails, setEventVideoDetails] =
    useState<CameraEventGetVideoDetailResponse | null>(null);
  const [
    eventGetVideoDetailsAPITrigger,
    {
      isLoading: isLoadinVideoDetails,
      isUninitialized: isUninitializedVideoDetails,
    },
  ] = useEventGetVideoDetailsMutation();
  const [downloadEventVideoApiTrigger, { isLoading: isDownloadingVideo }] =
    useGetEventVideoDownloadUrlMutation();

  useEffect(() => {
    if (!!deviceEventFromState?.ticket && isUninitializedVideoDetails) {
      eventGetVideoDetailsAPITrigger({
        ticketId: deviceEventFromState?.ticket,
      })
        .unwrap()
        .then((res) => {
          if (!!res.data) setEventVideoDetails(res.data);
          else {
            if (APP_CONFIG.DEBUG.ALL)
              console.log("Failed to retrieve event video details");
          }
        })
        .catch((e) => {
          if (APP_CONFIG.DEBUG.ALL)
            console.log("Error while retrieving event video details");
        });
    }
  }, [deviceEventFromState?.ticket, isUninitializedVideoDetails]);

  const ondownloadEventVideoClick = () => {
    if (!deviceEventFromState?.ticket) {
      console.error("No ticket available for downloading video");
      return;
    }
    dispatch(
      setModalsData({ ...modalsState, showEventDownloadConfirmation: true })
    );
  };

  const { data: dataDeviceListing, isFetching: isFetchingDeviceListing } =
    useGetDeviceListingQuery(
      {
        keyword: deviceEventFromState.cameraid,
        model: "",
        status: "",
        offset: 0,
        limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      },
      {
        refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
      }
    );

  // device event list for prior events list
  const refDateStringInTimezone = formattedDateTimeBasedOnTimezone(
    dbTimeToJsTime(deviceEventFromState?.timestamp || 0),
    tzForDateFns,
    "yyyy-MM-dd"
  );
  const { startOfDayInUTC } = getStartEndOfDayInUTC(
    refDateStringInTimezone,
    tzForDateFns
  );
  const {
    data: dataEventPriorEventsList,
    isFetching: isFetchingEventPriorEventsList,
  } = useGetDeviceEventsListQuery(
    {
      serial_number: "",
      start_time: jsTimeToDBTime(startOfDayInUTC.getTime()),
      end_time: deviceEventFromState?.timestamp - 1, // one second prior
      page_size: 10,
      page: 1,
    },
    {
      skip: false,
    }
  );

  // used to filter out ignored events to display accurate count
  const [filteredADASAIEvents, setFilteredADASAIEvents] = useState<
    DeviceEvent[]
  >([]);
  useEffect(() => {
    if (
      !!dataEventPriorEventsList &&
      !isFetchingEventPriorEventsList &&
      Array.isArray(dataEventPriorEventsList?.results)
    ) {
      setFilteredADASAIEvents(
        dataEventPriorEventsList?.results.filter(
          (item) => !IGNORED_EVENTS.includes(item.status.description)
        )
      );
    }
  }, [dataEventPriorEventsList, isFetchingEventPriorEventsList]);

  // START: Mechanism to retrieve video
  const [retrievingVideo, setRetrievingVideo] = useState<boolean>(false);
  const [retrievedVideoURL, setRetrievedVideoURL] = useState<string | boolean>(
    false
  );
  const [
    vMCameraEventGetVideoDetailResponse,
    setVMCameraEventGetVideoDetailResponse,
  ] = useState<CameraEventGetVideoDetailResponseVideos | null>(null);
  const [requestVideo, { isLoading: isLoadingCameraRequestVideo }] =
    useRequestVideoMutation();
  const [taskId, setTaskId] = useState<string | null>(); // Task Id
  const [startTaskUpdates, setStartTaskUpdates] = useState<boolean>(false); // Flag to trigger task updates on task creation
  const { data: taskStatusResponse, isLoading: isLoadingTaskStatus } =
    useGetTaskStatusByIdQuery(taskId ?? "", {
      skip: !taskId || !startTaskUpdates,
      pollingInterval: 1 * 1000,
      skipPollingIfUnfocused: true,
    });
  let taskStatusData = taskStatusResponse?.data ?? null;

  /**
   * Method to reset all timline video task related flags
   */
  const resetTask = useCallback((clearCurrentVideo = true) => {
    setTaskId(null);
    // setDownloadingVideo(false);
    // setPlayRequested(false);
    setStartTaskUpdates(false);
    setEventVideoDetails(null);
    setRetrievingVideo(false);
    if (clearCurrentVideo) {
      // setTimelineVideoDetails(null);
    }
  }, []);

  /**
   * Effect to download/play timeline video once it is available from API
   */
  useEffect(() => {
    if (!taskStatusData) {
      return;
    }
    if (taskStatusData?.process_state === "success") {
      if (
        // selectedTimelineData &&
        taskStatusData?.videos &&
        taskStatusData?.videos?.length > 0
      ) {
        const videoUrl = taskStatusData?.videos[0].video_url;
        toast.success(
          "The video was retrieved successfully and will start playing shortly."
        );
        setRetrievedVideoURL(videoUrl);
        if (APP_CONFIG.DEBUG.ALL)
          console.log("|| ====== >> VIDEO URL", videoUrl);
        // setTimeout added to allow the state to set
        onPlayEventVideoClick?.(videoUrl);
        resetTask(false);
      }
    } else if (taskStatusData?.process_state === "failed") {
      resetTask();
    }
  }, [
    // playRequested,
    // downloadingVideo,
    // selectedTimelineData,
    taskStatusData,
    // togglePlay,
    resetTask,
  ]);

  useEffect(() => {
    // Reset current timeline task status when task is completed(success or failed)
    if (
      taskStatusData?.process_state === "success" ||
      taskStatusData?.process_state === "failed"
    ) {
      setStartTaskUpdates(false);
      // setDownloadingVideo(false);
      // setPlayRequested(false);
      setTaskId(null);
    }
  }, [/*selectedEvent, selectedTimelineData, */ taskStatusData]);

  /**
   * Effect to reset any running task process on new event or video event selection
   */
  useEffect(() => {
    resetTask();
  }, [resetTask /*, selectedEvent, selectedTimelineData*/]);

  // Get Device config
  const { data: cameraConfigResponse, isLoading } =
    useGetCameraConfigQuery(cameraId);
  const cameraConfig = cameraConfigResponse?.data ?? null;
  const { data: deviceDetailRespsonse, isLoading: isLoadingDeviceData } =
    useGetCameraDetailsQuery(
      cameraListItem?.cid ?? "", // cameraId here was actually the serial number
      {
        skip: !cameraListItem?.cid, // cameraId
      }
    );
  const dataDeviceDetails =
    deviceDetailRespsonse?.data && deviceDetailRespsonse?.data?.length > 0
      ? deviceDetailRespsonse?.data[0]
      : null;
  /**
   * Recording type of camera
   * If recording type is Separate, then individual camera clip can be requested
   * If recording type is Side-By-Side, then individual camera calip cannot be requested (ALL should be requested)
   */
  const recordingType = useMemo(() => {
    if (cameraConfig?.current && cameraConfig?.current?.device) {
      const currentDeviceConfig = cameraConfig.current.device;
      const skuName = dataDeviceDetails?.sku_name?.replace("C", ""); // Remove C at the end to match config key
      const key =
        `vr_${skuName}_embedded_videoFormat` as keyof typeof currentDeviceConfig;
      const recordingType =
        key in currentDeviceConfig ? currentDeviceConfig[key] : undefined;
      return recordingType as RECORDING_TYPE;
    }
    return undefined;
  }, [dataDeviceDetails, cameraConfig]);

  // play video click handler
  const onPlayEventVideoClick = useCallback(
    (videoUrl?: string) => {
      const finalVideoUrl = videoUrl || retrievedVideoURL;
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "[onPlayEventVideoClick]",
          videoUrl,
          retrievedVideoURL,
          finalVideoUrl
        );
      // Commenting the code to play embedded video tag instead of opening a modal
      // if (!finalVideoUrl || typeof finalVideoUrl !== "string") return;
      // // set video URL information
      // setVMCameraEventGetVideoDetailResponse({
      //   videos: [
      //     {
      //       video_url: finalVideoUrl,
      //     } as any as VideoDetail,
      //   ],
      // });
      // // Show video modal with fetched details
      // dispatch(
      //   setModalsData({
      //     ...modalsState,
      //     showVehicleCameraVideo: true,
      //   })
      // );
      togglePlay();
    },
    [retrievedVideoURL]
  );

  // Toggle play/pause and manage playback state
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setPlaybackState(VIDEO_PLAYBACK_STATE.playing);
    } else {
      video.pause();
      setPlaybackState(VIDEO_PLAYBACK_STATE.paused);
    }
  }, [videoRef]);

  const handlePlay = () => setPlaybackState(VIDEO_PLAYBACK_STATE.playing);
  const handlePause = () => setPlaybackState(VIDEO_PLAYBACK_STATE.paused);
  const handleEnded = () => setPlaybackState(VIDEO_PLAYBACK_STATE.not_started);

  // Update playback state on video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
    };
  }, [videoRef]);

  /**
   * Commented retrieval mechanism. And use, event's own video for play
   */
  // finally, retrieve video click handler
  // const onRetrieveEventVideoClick = () => {
  //   const assetId = dataDeviceListing?.data?.[0]?.ft_device_id;
  //   if (!assetId) {
  //     toast.error("There is no asset associated with this camera device");
  //     return;
  //   }
  //   toast.info(
  //     "Retrieving video. This may take a couple of minutes. Please wait..."
  //   );
  //   setRetrievingVideo(true);
  //   requestVideo({
  //     timestamp: deviceEventFromState?.timestamp,
  //     serial_number: cameraId,
  //     duration_in_seconds: 30,
  //     cam_id:
  //       recordingType === RECORDING_TYPE.sideBySide
  //         ? CAMERA_CAPTURE_MODES.combined
  //         : CAMERA_CAPTURE_MODES.front,
  //     // device_id: assetId,
  //     // event_id: deviceEventFromState?.status.statuscode,
  //   })
  //     .unwrap()
  //     .then((res) => {
  //       // Get the task id from response and retrieve status of task
  //       const taskId = res?.data?.task_id;
  //       if (taskId) {
  //         setTaskId(taskId);
  //         setStartTaskUpdates(true); // Enable API to fetch task updates
  //       } else {
  //         resetTask();
  //         toast.error("Error requesting video");
  //       }
  //     })
  //     .catch((error) => {
  //       toast.error("Error requesting video");
  //       console.log("error requesting video", error);
  //       resetTask();
  //     });
  // };
  // END: Mechanism to retrieve video

  const onDownloadConfirm = () => {
    const fileName = `vid_${deviceEventFromState?.creationtime}` || "";
    if (eventVideoDetails?.videos && eventVideoDetails?.videos?.length > 0) {
      // Download the video file using the URL
      downloadEventVideo(eventVideoDetails?.videos?.[0]?.video_url, fileName);
    } else {
      downloadEventVideoApiTrigger({
        ticketId: deviceEventFromState?.ticket ?? "",
        fileName,
      })
        .then((res) => {
          if (res?.data?.download_url) {
            // Download the video file using the URL
            downloadEventVideo(res.data.download_url, fileName);
          } else {
            console.error("Failed to retrieve video download URL");
          }
        })
        .catch((error) => {
          console.error("Error downloading video:", error);
        });
    }
  };

  const MemoizedCameraVideoHistoryEventMap = useMemo(
    () => {
      return (
        <CameraVideoHistoryEventsMap
          compact={true}
          dataDeviceEventsList={
            !!deviceEventFromState.latitude && !!deviceEventFromState.longitude
              ? ({
                  results: [deviceEventFromState].filter(
                    (eventItem) => !!eventItem.latitude && !!eventItem.longitude
                  ),
                } as ListingResponse<DeviceEvent[]>)
              : undefined
          }
          isFetchingDeviceEventsList={false}
        />
      );
    },
    [
      // deviceEventFromState.latitude,
      // deviceEventFromState.longitude,
      // deviceEventFromState,
    ]
  );

  return (
    <>
      <div className="flex Xgrid Xgrid-cols-12 gap-6">
        {/* Left Column */}
        <div className="min-w-[300px] max-w-[300px] Xcol-span-3 flex flex-col gap-6 rounded-md bg-panel-gray2 px-3 py-4">
          {/* Driver Avatar & Details */}
          {(!!deviceEventFromState.drivername ||
            !!deviceEventFromState.driverid) && (
            <div className="flex items-center gap-4 break-words">
              <AppAvatar bgColorClass={"bg-gray-200"} initials={"-"} />
              <div className="">
                <div className="font-semibold">
                  {deviceEventFromState.drivername ||
                    deviceEventFromState.driverid || (
                      <div className="opacity-50 text-sm mb-1">
                        {"Driver information unavailable"}
                      </div>
                    )}
                </div>
                <div className="text-xs">
                  {formattedDateTimeBasedOnTimezone(
                    dbTimeToJsTime(deviceEventFromState.timestamp),
                    tzForDateFns,
                    APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Camera ID */}
          <div className="flex items-center gap-3">
            <img src={svgIcons.CameraEventSpecCamera} />
            {deviceEventFromState?.cameraid || cameraId || "-"}
          </div>

          {/* Expiry */}
          {(!!dataEventDetails?.data?.[0]?.expired ||
            !!dataEventDetails?.data?.[0]?.expiry) && (
            <div className="flex items-center gap-3">
              <img src={svgIcons.CameraEventSpecExpiry} />
              {!!dataEventDetails?.data?.[0]?.expired ? (
                t("expired")
              ) : !!dataEventDetails?.data?.[0]?.expiry ? (
                t("expiry", { days: dataEventDetails?.data?.[0]?.expiry })
              ) : (
                <span className="opacity-50 text-sm">
                  {"Expiration information unavailable"}
                </span>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <div className="w-1/2 relative flex flex-col justify-end">
                {APP_CONFIG.DEBUG.ALL && (
                  <pre className="text-[10px] bottom-full left-0">
                    ticketId: {JSON.stringify(!!deviceEventFromState?.ticket)}
                  </pre>
                )}
                <AdminFormFieldSubmit
                  label={t("download")}
                  variant={"secondary"}
                  customWrapperClass="w-full"
                  onClick={ondownloadEventVideoClick}
                  disabled={!eventVideoDetails?.videos?.[0]?.video_url}
                />
              </div>
              <div className="w-1/2 relative flex flex-col justify-end">
                {APP_CONFIG.DEBUG.ALL && (
                  <pre className="text-[10px] bottom-full left-0">
                    is_online:{" "}
                    {JSON.stringify(
                      dataDeviceListing?.data?.[0]?.is_online === "online"
                    )}
                    <br />
                    ft_device_id:{" "}
                    {JSON.stringify(
                      !!dataDeviceListing?.data?.[0]?.ft_device_id
                    )}
                  </pre>
                )}
                <AdminFormFieldSubmit
                  label={
                    <>
                      {retrievingVideo ? (
                        <img
                          src={svgIcons.LoadingAnimation}
                          className={`size-7`}
                          alt="Loading"
                        />
                      ) : (
                        <>
                          {playbackState === VIDEO_PLAYBACK_STATE.playing
                            ? t("pause_video")
                            : t("play_video")}
                        </>
                      )}
                    </>
                  }
                  variant={!!retrievedVideoURL ? "success" : "secondary"}
                  customWrapperClass="w-full"
                  onClick={
                    eventVideoDetails?.videos?.[0]?.video_url
                      ? () => {
                          onPlayEventVideoClick(
                            eventVideoDetails?.videos?.[0]?.video_url
                          );
                        }
                      : () => null
                    // : onRetrieveEventVideoClick
                  }
                  // retrieve button should activate only when camera is online and associated ft_device_id exists
                  disabled={!eventVideoDetails?.videos?.[0]?.video_url}
                />
              </div>
            </div>
            {!!deviceEventFromState?.ticket && (
              <AdminFormFieldSubmit
                label={`${t("comments")}${(dataEventComments?.data as EventComment[])?.length > 0 ? ` (${dataEventComments?.data.length})` : ""}`}
                variant={"secondary"}
                onClick={() => {
                  setCommentsForTicket(deviceEventFromState?.ticket);
                  dispatch(
                    setModalsData({ ...modalsState, showEventComments: true })
                  );
                }}
              />
            )}
          </div>

          {/* Map */}
          {MemoizedCameraVideoHistoryEventMap}

          {/* Details */}
          <div className="flex justify-between">
            <div className="">Address</div>
            <div className="font-semibold ml-2">
              {deviceEventFromState?.address || "Not Available"}
            </div>
          </div>
          <div className="flex justify-between">
            <div className="">Lat/Long</div>
            <div className="font-semibold">
              {deviceEventFromState.latitude}
              &deg;, {deviceEventFromState.longitude}
              &deg;
            </div>
          </div>
          <div className="flex justify-between">
            <div className="">Heading</div>
            <div className="font-semibold">
              {deviceEventFromState.heading}&deg;
            </div>
          </div>
          <div className="flex justify-between">
            <div className="">Altitude (m)</div>
            <div className="font-semibold">
              {deviceEventFromState?.altitude || "-"}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex-grow Xcol-span-9 flex flex-col gap-6 relative">
          {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
            <pre className="text-[8px] absolute bg-white/50 z-10 bottom-24 right-0 max-h-48 w-48 overflow-auto">
              {JSON.stringify(deviceEventFromState, null, 2)}
              <br />
              cameraListItem: {JSON.stringify(cameraListItem, null, 2)}
            </pre>
          )}
          {/* CAMERA: {cameraId} EVENT: {eventRecordId} */}
          {!!eventVideoDetails ? (
            <>
              {/* Video */}
              {(eventVideoDetails.videos || []).map((videoItem, i) => {
                const videoDimensionsAvailable =
                  !!videoItem.video_width && !!videoItem.video_height;
                const heightPerc = (
                  (videoItem.video_height * 100) /
                  videoItem.video_width
                ).toFixed(2);
                return (
                  <React.Fragment key={i}>
                    {/* <div
                      className={`relative w-full bg-black rounded-lg`}
                      style={
                        videoDimensionsAvailable
                          ? { paddingTop: `${heightPerc}%` }
                          : {}
                      }
                    >
                      <video
                        className={`w-full ${videoDimensionsAvailable ? `dim-available absolute top-0 left-0 h-full shadow-lg` : "h-[calc(75vh)]"} rounded-lg`}
                        controls
                        // autoPlay
                        src={videoItem.video_url}
                        ref={videoRef}
                        onEnded={handleEnded}
                        onPlay={handlePlay}
                        onPause={handlePause}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div> */}
                    <AppVideoElement
                      ref={videoRef}
                      controls
                      fixedHeightClass="h-[calc(75vh)]"
                      muted
                      onEnded={handleEnded}
                      onPlay={handlePlay}
                      onPause={handlePause}
                      playsInline
                      poster={videoItem.screenshot_url || undefined}
                      videoAddress={
                        deviceEventFromState?.address || "Address not available"
                      }
                      src={videoItem.video_url}
                      stretchPosterImage={
                        !!videoItem?.capture_mode &&
                        [
                          CAMERA_CAPTURE_MODES.grid, // combined video from 4TVI cameras with GRID
                          CAMERA_CAPTURE_MODES.combined, // combined video from the front and in-cabin 2TVI cameras with S_F_I
                        ].includes(
                          `${videoItem?.capture_mode}` as CAMERA_CAPTURE_MODES
                        )
                          ? true // fill to stretch the poster to align with the video properly
                          : false
                      }
                      useVideoARForDimension={
                        !!videoItem &&
                        !!videoItem.video_width &&
                        !!videoItem.video_height
                          ? videoItem
                          : undefined
                      }
                    />
                  </React.Fragment>
                );
              })}

              {/* Parking Photo */}
              {eventVideoDetails?.snapshots?.length > 0 && (
                <div className="relative w-full h-[calc(75vh)] bg-black rounded-lg">
                  <ParkingPhotos
                    photos={
                      eventVideoDetails?.snapshots?.map((snapshot) => {
                        return {
                          type: 1,
                          image: snapshot.url,
                        };
                      }) as DeviceSnapshot[]
                    }
                    displayAsModal={false}
                  />
                </div>
              )}
              {/* <pre>{JSON.stringify(eventVideoDetails, null, 2)}</pre> */}
            </>
          ) : null}

          <div className="w-full bg-white rounded-lg border border-heading-gray888/25 shadow-lg px-4 py-8 grid grid-cols-12 gap-6 mb-12">
            <h3 className="col-span-12 text-xl font-medium">Event Info</h3>
            <h4 className="col-span-12 text-lg font-semibold mt-4">
              Vehicle and Driver Behaviour Data
            </h4>
            <EventSpecItem
              title="Camera ID"
              value={
                deviceEventFromState?.cameraid || cameraId || "Not Available"
              }
            />
            <EventSpecItem
              title="Asset ID"
              value={deviceEventFromState?.deviceid || "Not Available"}
            />
            <EventSpecItem
              title="Description"
              value={
                deviceEventFromState?.device_description || "Not Available"
              }
            />
            <EventSpecItem
              title="Speed"
              value={
                !isNaN(deviceEventFromState?.speedkph) &&
                deviceEventFromState?.speedkph >= 0
                  ? `${Math.round(
                      convertKmphTo(speedUnit, deviceEventFromState?.speedkph)
                    ).toString()} ${
                      SPEED_UNITS_OPTIONS.find(
                        (option) => option.unit === speedUnit
                      )?.label || SPEED_UNITS_OPTIONS[0].label
                    }` // rounding off per client requirement
                  : "Not Available"
              }
            />
            <hr className="col-span-12" />
            <h4 className="col-span-12 text-lg font-semibold mt-4">
              Event Data
            </h4>
            <EventSpecItem
              title="Event Date"
              value={
                !!deviceEventFromState?.timestamp
                  ? formattedDateTimeBasedOnTimezone(
                      dbTimeToJsTime(deviceEventFromState?.timestamp),
                      convertToTZForDateFns(tzForDateFns),
                      APP_CONFIG.DATETIME_FORMATS.DATEPICKER.DATE_ONLY
                    )
                  : "Not Available"
              }
            />
            <EventSpecItem
              title="Event Time"
              value={
                !!deviceEventFromState?.timestamp
                  ? formattedDateTimeBasedOnTimezone(
                      dbTimeToJsTime(deviceEventFromState?.timestamp),
                      convertToTZForDateFns(tzForDateFns),
                      APP_CONFIG.DATETIME_FORMATS.DATEPICKER.TIME_ONLY
                    )
                  : "Not Available"
              }
            />
            {/* Address disabled here because it is displayed on the right */}
            {/* <EventSpecItem
              title="Address"
              value={deviceEventFromState?.address || "Not Available"}
            /> */}
            <EventSpecItem
              title="Event Type"
              value={
                !!deviceEventFromState?.status?.description
                  ? RENAMED_EVENTS.find(
                      (item) =>
                        item.backendValue ===
                        deviceEventFromState?.status?.description
                    )?.displayValue || deviceEventFromState?.status?.description
                  : "Not Available"
              }
            />
            <hr className="col-span-12" />
            <h4 className="col-span-12 text-lg font-semibold mt-4">
              Driver Information
            </h4>
            <EventSpecItem
              title="Driving Hours Today"
              value={
                !!dataDeviceEventDetails?.driving_time_in_sec
                  ? humanReadableTimeDuration(
                      dataDeviceEventDetails?.driving_time_in_sec * 1000,
                      false
                    )
                  : "Not Available"
              }
            />
            <EventSpecItem
              title="Coaching History"
              value={
                (dataEventComments?.data as EventComment[])?.length > 0
                  ? `${dataEventComments?.data.length} comments found`
                  : "None"
              }
            />
            <hr className="col-span-12" />
            <h4 className="col-span-12 text-lg font-semibold mt-4">
              System-Detected Violations
            </h4>
            {/* compact or detailed view */}
            {false ? (
              <EventSpecItem
                title={`Other ADAS or AI Alerts`}
                value={
                  dataEventPriorEventsList?.count.toString() || "Not Available"
                }
              />
            ) : (
              <EventSpecItem
                title={`Other ADAS or AI Alerts${!!filteredADASAIEvents.length ? ` (${filteredADASAIEvents.length})` : ""}`}
                value={
                  filteredADASAIEvents.length > 0
                    ? (
                        filteredADASAIEvents.reduce(
                          (acc, item) =>
                            acc.includes(item.status.description)
                              ? acc
                              : [...acc, item.status.description],
                          [] as string[]
                        ) || [""]
                      )
                        // remove ignored events
                        // disabled filtration here
                        // .filter((item) => !IGNORED_EVENTS.includes(item))
                        // rename events
                        .map(
                          (item) =>
                            RENAMED_EVENTS.find(
                              (_item) => _item.backendValue === item
                            )?.displayValue || item
                        )
                        .join(", ")
                    : "Not Available"
                }
              />
            )}
            {/* Removed this as count (above) is all that we have */}
            {/* <EventSpecItem title="Manual Triggers" value={"-"} /> */}
          </div>
          {/* <pre className="text-[8px]">
            {JSON.stringify(dataEventPriorEventsList, null, 2)}
          </pre> */}
        </div>
      </div>

      {/* Modals */}
      <ModalDownloadConfirmation handleDownload={onDownloadConfirm} />
      {!!commentsForTicket && (
        <ModalEventComments ticketId={commentsForTicket} />
      )}
      {!!vMCameraEventGetVideoDetailResponse && (
        <ModalCameraVideo
          data={{} as any as TRecordingDetails}
          {...(!!vMCameraEventGetVideoDetailResponse
            ? {
                vmData: vMCameraEventGetVideoDetailResponse,
                onClose: () => {
                  setVMCameraEventGetVideoDetailResponse(null);
                },
              }
            : {})}
        />
      )}
    </>
  );
};

export default ScreenCameraVideoHistoryEventDetails;
